import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowUpRight, FileDown, Lock, RefreshCw } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LINKEDIN_URL } from "@/data/content";
import { Chapter } from "./Chapter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t, lang } = useLang();
  const pdfHref = `/downloads/Klaus_Baumgart_Portfolio_${lang === "de" ? "DE" : "EN"}.pdf`;
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [answer, setAnswer] = useState("");

  const loadCaptcha = async () => {
    setAnswer("");
    try {
      const r = await axios.get(`${API}/captcha`);
      setCaptcha(r.data);
    } catch {
      setCaptcha(null);
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !answer) {
      toast.error(t.contact.toastValidation);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, captcha_id: captcha?.id || "", captcha_answer: answer });
      toast.success(t.contact.toastSuccess);
      setForm({ name: "", company: "", email: "", message: "" });
      loadCaptcha();
    } catch (err) {
      toast.error(err?.response?.status === 400 ? t.contact.toastCaptcha : t.contact.toastError);
      loadCaptcha();
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 px-6 lg:px-10 max-w-7xl mx-auto">
      <Chapter number="05" overline={t.chapters.contact.overline} title={t.chapters.contact.title} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <h3 className="font-heading font-extrabold tracking-tighter text-3xl md:text-5xl text-paper leading-tight">
            {t.contact.headlinePre}<span className="text-volt">{t.contact.headlineAccent}</span>
          </h3>
          <p className="font-body text-sm md:text-base text-dim mt-6 leading-relaxed max-w-md">
            {t.contact.body}
          </p>

          <div className="mt-12 space-y-5 font-mono text-xs md:text-sm">
            <div className="flex gap-6">
              <span className="text-dim w-28 shrink-0 tracking-[0.2em] uppercase">{t.contact.baseLabel}</span>
              <span className="text-paper">{t.contact.baseValue}</span>
            </div>
            <div className="flex gap-6">
              <span className="text-dim w-28 shrink-0 tracking-[0.2em] uppercase">{t.contact.openLabel}</span>
              <span className="text-paper">{t.contact.openValue}</span>
            </div>
            <div className="flex gap-6">
              <span className="text-dim w-28 shrink-0 tracking-[0.2em] uppercase">{t.contact.linkedinLabel}</span>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin-link"
                className="text-volt hover:text-paper inline-flex items-center gap-1 group"
              >
                /in/klaus-baumgart
                <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>
          </div>

          <a
            href={pdfHref}
            download
            data-testid="contact-download-cv-button"
            className="inline-flex items-center gap-3 mt-10 border border-volt text-volt font-mono text-xs uppercase tracking-[0.25em] px-6 py-3.5 hover:bg-volt hover:text-ink transition-colors duration-300 group"
          >
            <FileDown size={15} className="group-hover:translate-y-0.5 transition-transform" />
            {t.hero.downloadCv}
          </a>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="bg-graphite/80 backdrop-blur-md border border-line p-8 md:p-10 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim block mb-2">{t.contact.nameLabel}</label>
              <Input data-testid="contact-name-input" value={form.name} onChange={update("name")} placeholder={t.contact.namePh} className="bg-ink border-line rounded-none h-12 font-body focus-visible:ring-volt" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim block mb-2">{t.contact.companyLabel}</label>
              <Input data-testid="contact-company-input" value={form.company} onChange={update("company")} placeholder={t.contact.companyPh} className="bg-ink border-line rounded-none h-12 font-body focus-visible:ring-volt" />
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim block mb-2">{t.contact.emailLabel}</label>
            <Input data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} placeholder={t.contact.emailPh} className="bg-ink border-line rounded-none h-12 font-body focus-visible:ring-volt" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim block mb-2">{t.contact.messageLabel}</label>
            <Textarea data-testid="contact-message-input" value={form.message} onChange={update("message")} placeholder={t.contact.messagePh} rows={5} className="bg-ink border-line rounded-none font-body focus-visible:ring-volt" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim block mb-2">{t.contact.captchaLabel}</label>
            <div className="flex items-center gap-3">
              <span data-testid="captcha-question" className="font-mono text-lg text-volt whitespace-nowrap">
                {captcha ? `${captcha.question} =` : "..."}
              </span>
              <Input
                data-testid="captcha-answer-input"
                value={answer}
                onChange={(e) => setAnswer(e.target.value.replace(/\D/g, ""))}
                placeholder={t.contact.captchaPh}
                inputMode="numeric"
                className="bg-ink border-line rounded-none h-12 font-mono w-28 focus-visible:ring-volt"
              />
              <button type="button" onClick={loadCaptcha} data-testid="captcha-refresh-button" aria-label="Refresh captcha" className="text-dim hover:text-volt p-2">
                <RefreshCw size={16} />
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="w-full bg-volt text-ink font-mono text-sm uppercase tracking-[0.25em] py-4 hover:bg-paper disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? t.contact.sending : t.contact.submit}
          </button>
          <p className="font-mono text-[10px] text-dim flex items-center gap-2 tracking-wide">
            <Lock size={11} className="text-volt" /> {t.contact.note}
          </p>
        </motion.form>
      </div>
    </section>
  );
};
